import { Controller } from "../../lib/stimulus-shim"

/**
 * profiles--webmcp controller
 *
 * Enregistre les tools WebMCP sur le profil courant.
 * Feature-detected : ne fait rien si navigator.modelContext n'existe pas.
 *
 * Donnees :
 *   - Inline : <script id="mcp-context"> (identite, tools disponibles)
 *   - API : /api/mcp/profiles/:slug/* (donnees lourdes a la demande)
 *
 * Tracking :
 *   Chaque execute() envoie un beacon POST /api/mcp/trace
 */
export default class extends Controller {
  static values = {
    slug: String,
    traceUrl: { type: String, default: "/api/mcp/trace" }
  }

  connect() {
    if (!navigator.modelContext) return

    this._registeredToolNames = []
    this.context_data = this._readInlineContext()
    if (!this.context_data) return

    this._registerTools()
  }

  disconnect() {
    if (navigator.modelContext) {
      this._registeredToolNames.forEach(name => {
        try { navigator.modelContext.unregisterTool?.(name) } catch (e) {}
      })
    }
    this._registeredToolNames = []
  }

  // === PRIVATE ===

  _readInlineContext() {
    const el = document.getElementById("mcp-context")
    if (!el) return null

    try {
      return JSON.parse(el.textContent)
    } catch (e) {
      console.warn("[WebMCP] Failed to parse inline context:", e)
      return null
    }
  }

  _registerTools() {
    const tools = []
    const ctx = this.context_data
    const slug = ctx.slug
    const verb = ctx.verb

    // Tool: get_profile
    tools.push({
      name: "get_profile",
      description: "Get the full identity and metadata of this creator: name, bio, avatar, tags, social links, and Schema.org structured data.",
      inputSchema: { type: "object", properties: {}, required: [] },
      execute: async () => {
        this._trace(slug, "get_profile", verb)
        const response = await fetch(`/api/mcp/profiles/${slug}`)
        return response.json()
      },
      annotations: { readOnlyHint: true }
    })

    // Tool: list_modules
    tools.push({
      name: "list_modules",
      description: "List all active content modules on this profile with their types and positions.",
      inputSchema: { type: "object", properties: {}, required: [] },
      execute: async () => {
        this._trace(slug, "list_modules", verb)
        return { tools: ctx.tools, slug: ctx.slug, name: ctx.name }
      },
      annotations: { readOnlyHint: true }
    })

    // Tools dynamiques selon les modules actifs
    const toolNames = (ctx.tools || []).map(t => t.name)

    if (toolNames.includes("get_videos")) {
      tools.push({
        name: "get_videos",
        description: "Get YouTube videos published by this creator with titles, thumbnails, view counts, and durations.",
        inputSchema: {
          type: "object",
          properties: {
            limit: { type: "number", description: "Maximum number of videos to return (default 8)" }
          },
          required: []
        },
        execute: async () => {
          this._trace(slug, "get_videos", verb)
          const response = await fetch(`/api/mcp/profiles/${slug}/videos`)
          return response.json()
        },
        annotations: { readOnlyHint: true }
      })
    }

    if (toolNames.includes("get_audio")) {
      tools.push({
        name: "get_audio",
        description: "Get audio tracks or podcast episodes with titles, duration, and streaming links.",
        inputSchema: { type: "object", properties: {}, required: [] },
        execute: async () => {
          this._trace(slug, "get_audio", verb)
          const response = await fetch(`/api/mcp/profiles/${slug}/audio`)
          return response.json()
        },
        annotations: { readOnlyHint: true }
      })
    }

    if (toolNames.includes("get_membership")) {
      tools.push({
        name: "get_membership",
        description: "Get membership/support information including platform, tiers, and how to support this creator.",
        inputSchema: { type: "object", properties: {}, required: [] },
        execute: async () => {
          this._trace(slug, "get_membership", verb)
          const response = await fetch(`/api/mcp/profiles/${slug}/membership`)
          return response.json()
        },
        annotations: { readOnlyHint: true }
      })
    }

    try {
      tools.forEach(tool => {
        navigator.modelContext.registerTool(tool)
        this._registeredToolNames.push(tool.name)
      })
    } catch (e) {
      console.warn("[WebMCP] Failed to register tools:", e)
    }
  }

  /**
   * Envoie un ping de tracking via Beacon API (fire-and-forget)
   */
  _trace(slug, toolName, verb) {
    const payload = JSON.stringify({
      slug: slug,
      tool_name: toolName,
      verb: verb || null
    })

    try {
      navigator.sendBeacon(this.traceUrlValue, new Blob([payload], { type: "application/json" }))
    } catch (e) {
      fetch(this.traceUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true
      }).catch(() => {})
    }
  }
}
